import { Info as InfoIcon, Warning as WarningIcon } from "@mui/icons-material";
import { Grid2, Stack, Tooltip } from "@mui/material";
import * as Sentry from "@sentry/react";
import { omit } from "lodash";
import { ReactElement } from "react";
import { FieldValues, useFormContext } from "react-hook-form";

import { FormType } from "../..";

import { generateDynamicOptions } from "@/components/form/helpers/generateDynamicOptions";
import { FieldSchema } from "@/components/section";
import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import { useDropDownOptions } from "@/features/dropdown/api";

export interface FieldToolsWrapperProps<TFormValues extends FieldValues, TFieldSchema extends FieldValues, TRecord> {
  fieldSchema: TFieldSchema;
  renderField: (props: Exclude<TFieldSchema, FieldSchema<TFormValues, TRecord>>) => ReactElement;
  formType?: FormType;
}

export function FieldToolsWrapper<TFormValues extends FieldValues, TFieldSchema extends FieldValues, TRecord>({
  fieldSchema,
  renderField,
  formType = "create",
}: FieldToolsWrapperProps<TFormValues, TFieldSchema, TRecord>) {
  const { getValues } = useFormContext<TFormValues>();
  const { dropdownOptions } = useDropDownOptions({
    options: { queryKey: ["dropdownOptions"], enabled: Boolean(fieldSchema.dynamicOptions) },
  });
  const values = getValues();

  if ("getModifySubmissionValues" in fieldSchema && fieldSchema.getModifySubmissionValues) {
    fieldSchema = {
      ...fieldSchema,
      modifySubmissionValues: fieldSchema.getModifySubmissionValues(values),
    } as TFieldSchema;
  }

  if ("dynamicOptions" in fieldSchema && fieldSchema.dynamicOptions) {
    fieldSchema = {
      ...fieldSchema,
      options: generateDynamicOptions(fieldSchema.dynamicOptions, dropdownOptions),
    } as TFieldSchema;
  }

  if ("getEndpoint" in fieldSchema && fieldSchema.getEndpoint) {
    fieldSchema = { ...fieldSchema, endpoint: fieldSchema.getEndpoint(values) } as TFieldSchema;
  }

  if ("getSaveEndpoint" in fieldSchema && fieldSchema.getSaveEndpoint) {
    fieldSchema = { ...fieldSchema, saveEndpoint: fieldSchema.getSaveEndpoint(values) } as TFieldSchema;
  }

  if ("getDisabled" in fieldSchema && fieldSchema.getDisabled) {
    fieldSchema = { ...fieldSchema, disabled: fieldSchema.getDisabled(values, formType) } as TFieldSchema;
  }

  if ("getRequired" in fieldSchema && fieldSchema.getRequired) {
    fieldSchema = { ...fieldSchema, required: fieldSchema.getRequired(values) } as TFieldSchema;
  }

  if ("getLink" in fieldSchema && fieldSchema.getLink) {
    fieldSchema = { ...fieldSchema, link: fieldSchema.getLink(values) } as TFieldSchema;
  }

  if ("getHidden" in fieldSchema && fieldSchema.getHidden) {
    if (fieldSchema.getHidden(values)) return null;
  }

  if (formType === "read-only") {
    fieldSchema = { ...fieldSchema, disabled: true };
  }

  return (
    <Grid2
      size={{
        xs:
          fieldSchema.xs ??
          {
            // Form fields
            "async-check-box-table": 12,
            "async-multi-select": 4,
            "async-select": 4,
            "multi-select": 4,
            "check-box": 4,
            "check-box-group": 12,
            date: 4,
            file: 12,
            "file-public": 12,
            "radio-group": 12,
            select: 4,
            text: 4,
            number: 4,
            lexical: 12,

            // Non-Form fields
            "attachment-view": 12,
            typography: 12,
            alert: 12,
            divider: 12,
            box: 12,
            link: 12,
          }[(fieldSchema as unknown as FieldSchema<TFormValues, TRecord>).type],
      }}
      {...(fieldSchema.gridSx && { sx: fieldSchema.gridSx })}
    >
      <Stack display={fieldSchema.info || fieldSchema.warning ? "flex" : "block"} direction="row" spacing={1}>
        <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
          {renderField(
            omit(fieldSchema, [
              "type",
              "schema",
              "dynamicOptions",
              "defaultValue",
              "getModifySubmissionValues",
              "getEndpoint",
              "getSaveEndpoint",
              "getDisabled",
              "getRequired",
              "getLink",
              "getHidden",
              "info",
              "warning",
              "xs",
              "gridSx",
            ]) as Exclude<TFieldSchema, FieldSchema<TFormValues, TRecord>>
          )}
        </Sentry.ErrorBoundary>

        {fieldSchema.info && (
          <Tooltip title={fieldSchema.info}>
            <InfoIcon color="secondary" fontSize="small" />
          </Tooltip>
        )}

        {fieldSchema.warning && (
          <Tooltip title={fieldSchema.warning}>
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        )}
      </Stack>
    </Grid2>
  );
}
